import { measureOperation } from '../../lib/operation-timing.ts'
import { z } from 'zod'
import type { OfflineDom, OfflineEl } from '../layout-shift/rrweb.ts'

const TEXT_SELECTOR_VALUE = z.string()
const normalize = (value: string) => value.replace(/\s+/g, ' ').trim()

// A text-bearing scope compound with an optional target inside it: `label:has-text("Email") input` is
// the form the catalog generates for a control labeled by a <label>, and the bare `button:has-text("Continue")`
// is the Playwright text pseudo a sightmap review authors from captured DOM evidence.
const HAS_TEXT = /^(.+?):has-text\(("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')\)(?:\s+(.+))?$/

// Generated text selectors append a self-delimiting `s` after the quoted payload; the standard bare
// text-engine form ends at the closing quote. Both carry the same JSON-quoted payload.
const textPayload = (selector: string, engine: string): string | null => {
  const body = selector.slice(engine.length, /"[si]$/.test(selector) ? -1 : undefined)
  try { return TEXT_SELECTOR_VALUE.parse(JSON.parse(body)) } catch { return null }
}

export function querySightmapSelector(dom: OfflineDom, selector: string, parent?: OfflineEl): OfflineEl[] {
  return measureOperation('polish/sightmap/query.querySightmapSelector', () => {
    if (selector.startsWith(':scope > ')) {
      if (!parent) return []
      return selector.slice(':scope > '.length).split(' > ').reduce<OfflineEl[]>(
        (nodes, part) => nodes.flatMap(node => node.children.filter(child => child.matches(part))), [parent])
    }
    if (selector.startsWith('internal:text=')) {
      const text = textPayload(selector, 'internal:text=')
      if (text === null) return []
      const matches = (value: string) => selector.endsWith('i')
        ? normalize(value).toLowerCase().includes(normalize(text).toLowerCase()) : normalize(value) === normalize(text)
      return dom.all().filter(element => matches(element.text) && !element.children.some(child => matches(child.text)))
    }
    // The bare has-text engine form: an element containing the text anywhere inside it.
    if (selector.startsWith('internal:has-text=')) {
      const text = textPayload(selector, 'internal:has-text=')
      if (text === null) return []
      const wanted = normalize(text).toLowerCase()
      return dom.all().filter(element => normalize(element.text).toLowerCase().includes(wanted))
    }
    const hasText = HAS_TEXT.exec(selector)
    if (hasText) {
      const quoted = hasText[2]!
      let wanted: string
      try {
        wanted = quoted.startsWith("'")
          ? quoted.slice(1, -1).replace(/\\(['\\])/g, '$1')
          : TEXT_SELECTOR_VALUE.parse(JSON.parse(quoted))
      } catch { return [] }
      const needle = normalize(wanted).toLowerCase()
      const scopes = dom.querySelectorAll(hasText[1]!).filter(element => normalize(element.text).toLowerCase().includes(needle))
      const targets = hasText[3] ? scopes.flatMap(scope => scope.querySelectorAll(hasText[3]!)) : scopes
      return [...new Map(targets.map(target => [target.id, target])).values()]
    }
    return dom.querySelectorAll(selector)
  })
}
